import React from "react";

import { faCircle, faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import styles from "./LoadingSpinner.module.css";

interface Props {
	/** Only show the spinner after this many milliseconds */
	delay?: number;
	/** If true, renders inline instead of absolutely centring; if "centered", renders inline, but center-aligned  */
	inline?: boolean | "centered";
	/** Render the loader inside an element that dims the nearest positioned ancestor */
	dimmer?: boolean;
	size?: FontAwesomeIconProps["size"];
	className?: string;
}

export default function LoadingSpinner({ delay, inline, size = "lg", dimmer = true, className }: Props) {
	const [show, setShow] = React.useState(!delay);

	React.useEffect(() => {
		if (!delay) return;
		const timeout = window.setTimeout(() => setShow(true), delay);
		return () => window.clearTimeout(timeout);
	}, [delay, setShow]);

	const loaderNode = (
		<span
			aria-label="Loading…"
			role="status"
			className={cx(className, "fa-layers fa-fw", styles.spinner, {
				[styles.fadeable]: !dimmer,
				[styles.hidden]: !dimmer && !show,
				[styles.inline]: Boolean(inline),
				[styles.centered]: inline === "centered",
			})}
		>
			<FontAwesomeIcon icon={faCircle} fixedWidth size={size} className={styles.spinnerBackground} />
			<FontAwesomeIcon
				icon={faSpinnerThird}
				fixedWidth
				spin
				size={size}
				style={{
					"--fa-animation-duration": "1s",
				}}
				className={styles.spinnerForeground}
			/>
		</span>
	);

	if (dimmer) return <div className={cx(styles.dimmer, styles.fadeable, { [styles.hidden]: !show })}>{loaderNode}</div>;
	return loaderNode;
}
